import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import NovelCard from '@/components/feature/NovelCard';
import { getNovelById, novels } from '@/mocks/novels';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const novel = id ? getNovelById(id) : undefined;

  if (!novel) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mb-4">
            <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-semibold text-foreground-950 mb-2">文章未找到</h1>
          <p className="text-foreground-500 mb-4">抱歉，您要查看的小说推荐不存在或已被移除。</p>
          <Link
            to="/"
            className="px-5 py-2 rounded-full bg-primary-500 text-white text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedNovels = novels
    .filter((n) => n.categorySlug === novel.categorySlug && n.id !== novel.id)
    .slice(0, 4);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${novel.title} - 可爱小说推荐`,
    description: novel.description,
    author: { '@type': 'Person', name: novel.author },
    datePublished: novel.publishDate,
    dateModified: novel.publishDate,
    image: novel.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '可爱小说推荐',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://keaixiaoshuo.com/article/${novel.id}`,
    },
  };

  const bookJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Book',
    name: novel.title,
    author: { '@type': 'Person', name: novel.author },
    description: novel.description,
    image: novel.coverImage,
    genre: novel.category,
    inLanguage: 'zh-CN',
    datePublished: novel.publishDate,
  };

  return (
    <>
      <SEO
        title={`${novel.title} - 可爱小说推荐 | ${novel.author}作品`}
        description={`${novel.title}：${novel.description.slice(0, 120)}。${novel.category}推荐，${novel.tags.join('、')}，评分${novel.rating}，${novel.wordCount}${novel.status}。可爱小说推荐平台精选。`}
        keywords={`${novel.title},${novel.author},${novel.category},${novel.tags.join(',')},可爱小说,可爱小说推荐`}
        canonicalUrl={`https://keaixiaoshuo.com/article/${novel.id}`}
        ogType="article"
        ogImage={novel.coverImage}
        articlePublishedTime={novel.publishDate}
        articleModifiedTime={novel.publishDate}
      />
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(bookJsonLd) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <article className="pt-20 md:pt-24">
          <div className="w-full px-4 md:px-6">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                { name: novel.category, path: `/category/${novel.categorySlug}` },
                { name: novel.title },
              ]}
            />
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
            {/* Header */}
            <header className="flex flex-col md:flex-row gap-6 md:gap-8 mb-10">
              <div className="shrink-0 w-40 md:w-48 mx-auto md:mx-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden border border-background-200/70">
                  <img
                    src={novel.coverImage}
                    alt={novel.title}
                    title={`${novel.title} - 可爱小说推荐`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex flex-col justify-center">
                <h1 className="text-xl md:text-3xl font-heading text-foreground-950 mb-3">
                  {novel.title}
                </h1>
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="text-sm text-foreground-600">
                    作者：<strong>{novel.author}</strong>
                  </span>
                  <span className="text-foreground-300">|</span>
                  <span className="inline-block px-2.5 py-0.5 text-xs rounded-full bg-secondary-100 text-secondary-800">
                    {novel.category}
                  </span>
                  <span className="text-foreground-300">|</span>
                  <span className="text-sm text-foreground-600">{novel.wordCount}</span>
                </div>

                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i
                      key={i}
                      className={`text-sm ${
                        i < Math.floor(novel.rating)
                          ? 'ri-star-fill text-amber-400'
                          : i < novel.rating
                            ? 'ri-star-half-fill text-amber-400'
                            : 'ri-star-line text-foreground-300'
                      }`}
                    ></i>
                  ))}
                  <span className="text-sm font-semibold text-foreground-800 ml-1">{novel.rating}</span>
                  <span className="text-xs text-foreground-400 ml-2">{novel.status}</span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {novel.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-block px-2.5 py-0.5 text-xs rounded-full bg-accent-100/70 text-accent-800"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <p className="text-xs text-foreground-500">
                  发布日期：{novel.publishDate}
                </p>
              </div>
            </header>

            {/* Content */}
            <div className="prose-custom">
              <section className="mb-10">
                <h2 className="text-lg md:text-xl font-heading text-foreground-950 mb-4">
                  小说简介
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  {novel.description}
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-lg md:text-xl font-heading text-foreground-950 mb-4">
                  精彩片段
                </h2>
                <blockquote className="border-l-4 border-primary-300 bg-primary-50/50 rounded-r-lg p-4 md:p-5">
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed italic">
                    {novel.excerpt}
                  </p>
                </blockquote>
              </section>

              <section className="mb-10">
                <h2 className="text-lg md:text-xl font-heading text-foreground-950 mb-4">
                  推荐理由
                </h2>
                <div className="flex flex-col gap-3">
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-background-100">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 shrink-0 mt-0.5">
                      <i className="ri-heart-line text-sm text-primary-500"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">温暖治愈的阅读体验</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        全书洋溢着温暖治愈的氛围，角色设定可爱讨喜，情节轻松愉快，是一本能够让你在疲惫时放松心情的好小说。
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-background-100">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 shrink-0 mt-0.5">
                      <i className="ri-star-line text-sm text-accent-500"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">性格鲜明的可爱角色</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        作者笔下的人物性格鲜明，每一个都让人忍不住想揉揉头。主角的成长线和感情线都处理得恰到好处，读起来非常过瘾。
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-background-100">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 shrink-0 mt-0.5">
                      <i className="ri-quill-pen-line text-sm text-secondary-500"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">清新甜美的文字风格</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        文笔清新流畅，描写细腻动人，把可爱和甜蜜的感觉写到了极致。读起来就像在吃一块奶油蛋糕，甜而不腻。
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-lg md:text-xl font-heading text-foreground-950 mb-4">
                  书籍信息
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {[
                    { label: '小说类型', value: novel.category },
                    { label: '总字数', value: novel.wordCount },
                    { label: '更新状态', value: novel.status },
                    { label: '作者', value: novel.author },
                    { label: '评分', value: `${novel.rating} / 5.0` },
                    { label: '发表日期', value: novel.publishDate },
                  ].map((info) => (
                    <div
                      key={info.label}
                      className="p-3 rounded-lg bg-background-100 border border-background-200/60"
                    >
                      <p className="text-xs text-foreground-500 mb-0.5">{info.label}</p>
                      <p className="text-sm font-medium text-foreground-800">{info.value}</p>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            {/* Tags Navigation */}
            <div className="flex flex-wrap items-center gap-2 py-6 border-t border-background-200/60">
              <span className="text-sm text-foreground-500 mr-1">标签：</span>
              {novel.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/category/${novel.categorySlug}`}
                  className="px-3 py-1 text-xs rounded-full bg-accent-100/70 text-accent-800 hover:bg-accent-200/70 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  #{tag}
                </Link>
              ))}
            </div>
          </div>
        </article>

        {/* Related Novels */}
        {relatedNovels.length > 0 && (
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-lg md:text-2xl font-heading text-foreground-950 mb-6 text-center">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedNovels.map((n) => (
                  <NovelCard key={n.id} novel={n} />
                ))}
              </div>
            </div>
          </section>
        )}

        <Footer />
      </div>
    </>
  );
}